/**
 * Trust entity hooks — fetch real users / custom-agents / automate-workflows
 * for the Trust module's filter dropdowns and row labels.
 *
 * These replace the hardcoded `ACTIVITY_USERS`, `ACTIVITY_AGENTS`, and
 * `ACTIVITY_WORKFLOWS` arrays from `lib/mock-trust-activity.ts`. Trust pages
 * use `useTrustEntities()` to get a unified `{ users, agents, workflows,
 * lookup }` view-model where `lookup.user(id)`, `lookup.agent(id)`, and
 * `lookup.workflow(id)` return the entity (or `null` if unknown).
 *
 * If a backend response does not include a name (e.g. user has only a
 * UUID), the lookup returns `null` and callers MUST fall back to the
 * raw id — never to a hardcoded display name.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from './api-client';

// ─── Server VMs ────────────────────────────────────────────────────────

interface ServerUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  status: string | null;
}

interface UsersListResponse {
  data: ServerUser[];
  pagination?: { page: number; pageSize: number; total: number };
}

interface ServerCustomAgent {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  category: string | null;
  ownership: string;
  status: string;
}

interface CustomAgentsListResponse {
  data: ServerCustomAgent[];
  total: number;
}

interface ServerWorkflow {
  id: string;
  name: string;
  description: string;
  status: string;
  agentId: string | null;
  agentName: string | null;
}

interface WorkflowsListResponse {
  workflows: ServerWorkflow[];
}

// ─── VM types exposed to UI ────────────────────────────────────────────

export interface TrustUser {
  id: string;
  name: string;
  email: string;
  role: string;
  /** false = hardcoded mock fallback would have been used; we leave 'unknown' instead */
  isPlaceholder?: boolean;
}

export interface TrustAgent {
  id: string;
  name: string;
  type: string;
  /** ID linking to /agents/$agentId — same as `id` for custom agents */
  linkedAgentId: string | null;
  iconColor: string;
}

export interface TrustWorkflow {
  id: string;
  name: string;
  /** ID linking to /automate/workflows/$id — same as `id` */
  linkedWorkflowId: string | null;
  iconColor: string;
}

// ─── Adapters ──────────────────────────────────────────────────────────

function userDisplayName(u: ServerUser): string {
  const first = u.firstName?.trim();
  const last = u.lastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  return u.email || u.id;
}

function adaptUser(u: ServerUser): TrustUser {
  return {
    id: u.id,
    name: userDisplayName(u),
    email: u.email ?? '',
    role: u.role ?? '—',
  };
}

function adaptAgent(a: ServerCustomAgent): TrustAgent {
  return {
    id: a.id,
    name: a.name,
    type: a.category ?? a.ownership ?? 'agent',
    linkedAgentId: a.id,
    iconColor: 'violet',
  };
}

function adaptWorkflow(w: ServerWorkflow): TrustWorkflow {
  return {
    id: w.id,
    name: w.name,
    linkedWorkflowId: w.id,
    iconColor: 'cyan',
  };
}

// ─── Hooks ─────────────────────────────────────────────────────────────

export function useTrustUsers() {
  return useQuery<UsersListResponse, Error, TrustUser[]>({
    queryKey: ['trust-entities', 'users'],
    queryFn: () =>
      apiClient.get<UsersListResponse>('/users?pageSize=100'),
    select: (r) => (r.data ?? []).map(adaptUser),
    staleTime: 60_000,
  });
}

export function useTrustAgents() {
  return useQuery<CustomAgentsListResponse, Error, TrustAgent[]>({
    queryKey: ['trust-entities', 'agents'],
    queryFn: () =>
      apiClient.get<CustomAgentsListResponse>('/custom-agents?view=all'),
    select: (r) => (r.data ?? []).map(adaptAgent),
    staleTime: 60_000,
  });
}

export function useTrustWorkflows() {
  return useQuery<WorkflowsListResponse, Error, TrustWorkflow[]>({
    queryKey: ['trust-entities', 'workflows'],
    queryFn: () =>
      apiClient.get<WorkflowsListResponse>('/automate-workflows'),
    select: (r) => (r.workflows ?? []).map(adaptWorkflow),
    staleTime: 60_000,
  });
}

export interface TrustEntities {
  users: TrustUser[];
  agents: TrustAgent[];
  workflows: TrustWorkflow[];
  isLoading: boolean;
  isError: boolean;
  lookup: {
    user: (id: string | null | undefined) => TrustUser | null;
    agent: (id: string | null | undefined) => TrustAgent | null;
    workflow: (id: string | null | undefined) => TrustWorkflow | null;
  };
}

/**
 * Convenience: wraps the three hooks above and exposes O(1) lookup maps.
 *
 * Lookups return `null` (not a fabricated "Unknown User") when the id is
 * not in the org. Callers should fall back to rendering the raw id.
 */
export function useTrustEntities(): TrustEntities {
  const usersQ = useTrustUsers();
  const agentsQ = useTrustAgents();
  const workflowsQ = useTrustWorkflows();

  const users = usersQ.data ?? [];
  const agents = agentsQ.data ?? [];
  const workflows = workflowsQ.data ?? [];

  const userMap = useMemo(() => {
    const m = new Map<string, TrustUser>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const agentMap = useMemo(() => {
    const m = new Map<string, TrustAgent>();
    for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);

  const workflowMap = useMemo(() => {
    const m = new Map<string, TrustWorkflow>();
    for (const w of workflows) m.set(w.id, w);
    return m;
  }, [workflows]);

  return {
    users,
    agents,
    workflows,
    isLoading: usersQ.isLoading || agentsQ.isLoading || workflowsQ.isLoading,
    isError: usersQ.isError || agentsQ.isError || workflowsQ.isError,
    lookup: {
      user: (id) => (id ? userMap.get(id) ?? null : null),
      agent: (id) => (id ? agentMap.get(id) ?? null : null),
      workflow: (id) => (id ? workflowMap.get(id) ?? null : null),
    },
  };
}

/**
 * Truncates an opaque UUID to a short, human-scannable id for display
 * when no real entity name is available. e.g. "abc12345-..." → "abc12345".
 */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  const noPrefix = id.replace(/^(user|agent|workflow):/, '');
  return noPrefix.length > 12 ? noPrefix.slice(0, 8) : noPrefix;
}
